import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { CanSeeModulesMarketplaceDocument } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useCheckModuleAcquired } from 'modules/marketplace_modules/hooks/useCheckModuleAcquired'
import SubscriptionButtons from 'modules/product_updates/components/Buttons/_SubscriptionButtons'
import UpsellButtons from 'modules/product_updates/components/UpsellButtons'
import { useGetProductUpdatesSubscriptions } from 'modules/product_updates/hooks/useGetProductUpdatesSubscriptions'

import ModuleStateIcon from './_ModuleStateIcon'

type Props = {
  productModule: {
    name: string
    urlIdentifier: string
  }
  chargebeeModuleName: string
  productUpdateSlug: string
  featureIdentifier?: string
  canSeeUpsellButton: boolean
  isAlreadyReleased: boolean
  productUpdateId: string
}

const UpsellProductDetail = ({
  productUpdateId,
  productModule,
  chargebeeModuleName,
  productUpdateSlug,
  featureIdentifier,
  canSeeUpsellButton,
  isAlreadyReleased,
}: Props) => {
  let isModuleAcquired: boolean =
    useCheckModuleAcquired(chargebeeModuleName) || productModule.urlIdentifier === 'factorial-core'
  const navigate = useNavigate()
  if (!chargebeeModuleName) isModuleAcquired = true
  const { navigation } = useNavigation()
  const { data: policies } = useQuery(CanSeeModulesMarketplaceDocument)
  const canSeeMarketplaceModules = policies?.modules._policies.moduleManageModule.can
  const { productUpdatesSubscriptions } = useGetProductUpdatesSubscriptions()

  const isAlreadySubscribed = (productUpdatesSubscriptions || []).some(
    (subscription) => subscription.productUpdateId === productUpdateId
  )

  const text = () => {
    if (!isAlreadyReleased && isAlreadySubscribed) {
      return i18n.t('product_updates.detail_page.upsell_banner.not_released_subscribed')
    }
    if (!isAlreadyReleased && !isAlreadySubscribed) {
      return i18n.t('product_updates.detail_page.upsell_banner.not_released', {
        module: productModule.name,
      })
    }
    if (isModuleAcquired) {
      return i18n.t('product_updates.detail_page.upsell_banner.already_have', {
        module: productModule.name,
      })
    }
    if (!isModuleAcquired && !canSeeUpsellButton && isAlreadyReleased) {
      return i18n.t('product_updates.detail_page.upsell_banner.learn', {
        module: productModule.name,
      })
    }
    if (!isModuleAcquired && canSeeUpsellButton && isAlreadyReleased) {
      return i18n.t('product_updates.detail_page.upsell_banner.upsell', {
        module: productModule.name,
      })
    }
  }

  return (
    <Box>
      <Box
        borderRadius={{ all: 'abs012' }}
        paddingX="s8"
        paddingY="s8"
        flexDirection="row"
        justifyContent="spaceBetween"
        style={{
          backgroundColor: !isAlreadyReleased
            ? 'rgba(75, 85, 99, 0.05)'
            : !isModuleAcquired
            ? 'rgb(245, 165, 28, 0.05)'
            : 'rgb(16, 184, 129, 0.05)',
        }}
        alignItems="center"
      >
        <Box flexDirection="row" alignItems="center" gap="s8">
          <ModuleStateIcon
            isModuleAcquired={isModuleAcquired}
            isAlreadyReleased={isAlreadyReleased}
            isAlreadySubscribed={isAlreadySubscribed}
          />

          <span className="text-base font-medium">{text()}</span>
        </Box>
        {!isModuleAcquired && isAlreadyReleased && (
          <UpsellButtons
            chargebeeModuleName={chargebeeModuleName}
            productUpdateSlug={productUpdateSlug}
            featureIdentifier={featureIdentifier}
            pageSource="module_availability_banner"
            version="factorial-one"
            disableIcon
            label={i18n.t('product_updates.detail_page.upsell_button')}
          />
        )}
        {!isAlreadyReleased && (
          <SubscriptionButtons
            productUpdateId={productUpdateId}
            productUpdateSlug={productUpdateSlug}
            version="factorial-one"
            source="module_availability_banner"
          />
        )}
        {!canSeeUpsellButton && !isModuleAcquired && canSeeMarketplaceModules && (
          <Button
            size="md"
            variant="outline"
            label="Learn more"
            onClick={() => {
              track(trackingEvents.marketplaceModules.goToModuleDetailPage, {
                origin: productUpdateSlug,
                source: 'module_availability_banner',
                module: productModule.urlIdentifier,
              })
              navigate(
                navigation.discover.marketplaceModules.fullPath() +
                  `/${productModule.urlIdentifier}?pm=false`
              )
            }}
          />
        )}
      </Box>
    </Box>
  )
}

export default UpsellProductDetail
